import React, { useRef, useState } from 'react';
import { ChevronDown, ShoppingCart, Minus, Plus, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { Product } from '@/types/product';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';

interface ProductDetailProps {
  product: Product;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ product }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>(product.sizes?.[0] || '');
  const [selectedImage, setSelectedImage] = useState(product.images[0]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToCart } = useCart();
  const { tl } = useLanguage();
  const detailsRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const scrollToDetails = () => {
    detailsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddToCart = () => {
    addToCart(product, quantity, selectedSize);
  };

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen w-full">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${product.images[0]})` }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        {product.discount != null && product.discount > 0 && (
          <div className="absolute top-0 left-0 right-0 w-full z-20">
            <div className="bg-red-600 text-white font-bold text-lg md:text-xl px-6 py-3 shadow-lg text-center">
              {tl('PROMOTION', 'PROMOTION', 'PROMOZIONE')} -{product.discount}%
            </div>
          </div>
        )}
        
        <div className="relative h-full flex flex-col items-center justify-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-sans text-white text-center px-4"
          >
            {product.name}
          </motion.h1>
          
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            onClick={scrollToDetails}
            className="absolute bottom-8 text-white animate-bounce"
          >
            <ChevronDown size={32} />
          </motion.button>
        </div>
      </section>

      {/* Gallery Section */}
      {product.images.length > 0 && (
        <motion.div 
          className="relative w-full overflow-hidden mt-20 bg-white"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Mobile Layout - Single Image with 1:1 aspect ratio */}
          <div className="md:hidden">
            <div className="relative w-full aspect-square">
              <img
                src={product.images[currentImageIndex]}
                alt="Current"
                className="w-full h-full object-cover cursor-pointer"
                onClick={openModal}
              />
              
              {/* Navigation Buttons for Mobile */}
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/30 p-2 rounded-full transition-colors"
                onClick={prevImage}
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/30 p-2 rounded-full transition-colors"
                onClick={nextImage}
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Desktop Layout - Three Image Carousel */}
          <div className="hidden md:block h-[80vh]">
            <div className="absolute inset-0 flex items-center">
              {/* Previous Image */}
              <motion.div 
                className="absolute left-0 w-1/4 h-full"
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={product.images[(currentImageIndex - 1 + product.images.length) % product.images.length]}
                  alt="Previous"
                  className="w-full h-full object-cover opacity-50"
                />
              </motion.div>

              {/* Current Image */}
              <motion.div 
                className="absolute left-1/4 w-1/2 h-full"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={product.images[currentImageIndex]}
                  alt="Current"
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={openModal}
                />
              </motion.div>

              {/* Next Image */}
              <motion.div 
                className="absolute right-0 w-1/4 h-full"
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={product.images[(currentImageIndex + 1) % product.images.length]}
                  alt="Next"
                  className="w-full h-full object-cover opacity-50"
                />
              </motion.div>
            </div>

            {/* Navigation Buttons for Desktop */}
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
              onClick={prevImage}
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
              onClick={nextImage}
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Fullscreen Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-8" onClick={closeModal}>
          <div
            className="relative flex items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative w-full max-w-2xl aspect-square bg-white rounded-lg shadow-2xl overflow-hidden">
              <img
                src={product.images[currentImageIndex]}
                alt={`Fullscreen product view ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
                draggable={false}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20"
              onClick={closeModal}
              aria-label="Close image modal"
            >
              <X className="h-6 w-6 text-white" />
            </Button>
          </div>
        </div>
      )}

      {/* Product Details Section */}
      <div ref={detailsRef} className="py-16 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-sans mb-4">{product.name}</h2>
              <p className="text-lg text-muted-foreground">{product.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-8">
              <div className="space-y-2">
                <h3 className="text-lg text-black font-medium">{tl('Matériau', 'Material', 'Materiale')}</h3>
                <div className="flex items-center space-x-2">
                  {product.material?.image_url && (
                    <img 
                      src={product.material.image_url} 
                      alt={product.material.name}
                      className="w-8 h-8 object-cover rounded"
                    />
                  )}
                  <p className="text-muted-foreground">{product.material?.name}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg text-black font-medium">{tl('Textile', 'Textile', 'Tessuto')}</h3>
                <div className="flex items-center space-x-2">
                  {product.textile?.image_url && (
                    <img 
                      src={product.textile.image_url} 
                      alt={product.textile.name}
                      className="w-8 h-8 object-cover rounded"
                    />
                  )}
                  <p className="text-muted-foreground">{product.textile?.name}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg text-black font-medium">{tl('Dimensions', 'Dimensions', 'Dimensioni')}</h3>
                <p className="text-muted-foreground">{product.dimensions}</p>
              </div>
            </div>

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg text-black font-medium">{tl('Taille', 'Size', 'Taglia')}</h3>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <Button
                      key={size}
                      variant={selectedSize === size ? 'default' : 'outline'}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selection */}
            <div className="flex items-center justify-center gap-4">
              <span className="text-lg text-black ">{tl('Quantité:', 'Quantity:', 'Quantità:')}</span>
              <div className="flex items-center border rounded-md">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4 text-black" />
                </Button>
                <span className="px-4 py-2 border-x text-black">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={incrementQuantity}
                >
                  <Plus className="h-4 w-4 text-black" />
                </Button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <div className="flex justify-center">
              <Button
                size="lg"
                variant="luxury"
                className="w-full md:w-auto"
                onClick={handleAddToCart}
                disabled={!product.inStock}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                {product.inStock
                  ? tl('Ajouter au panier', 'Add to Cart', 'Aggiungi al carrello')
                  : tl('Rupture de stock', 'Out of Stock', 'Esaurito')}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
